/* PrimeCut product page: gallery + thumbnails, native variant picker with pack pricing, quantity,
   sticky purchase link, purchase anchors, click-to-play videos, review filter/sort/pagination. */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  function initGallery(gallery) {
    if (gallery.dataset.pcReady) return;
    const track = gallery.querySelector('[data-pc-gal-track]');
    if (!track) return;
    gallery.dataset.pcReady = 'true';
    const slides = [...track.children];
    const thumbs = [...gallery.querySelectorAll('[data-pc-thumb]')];
    const list = gallery.querySelector('[data-pc-thumbs]');
    const prev = gallery.querySelector('[data-pc-gal-prev]');
    const next = gallery.querySelector('[data-pc-gal-next]');
    const counter = gallery.querySelector('[data-pc-gal-count]');
    let index = 0;
    let frame = 0;

    // The next slide peeks on tablet widths, so start loading it right away.
    const peek = slides[1] && slides[1].querySelector('img[loading="lazy"]');
    if (peek) peek.loading = 'eager';

    const setActive = (i) => {
      index = i;
      thumbs.forEach((thumb, n) => {
        if (n === i) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = String(i + 1);
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= slides.length - 1;
      const item = thumbs[i] && thumbs[i].parentElement;
      if (item && list) {
        // Thumbnails run in a row under the image on phones and tablets, and in a column beside it on desktop.
        const left = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
        const top = item.offsetTop - (list.clientHeight - item.offsetHeight) / 2;
        list.scrollTo({ left: Math.max(0, left), top: Math.max(0, top), behavior: behavior() });
      }
    };

    const goTo = (i) => {
      const target = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[target].offsetLeft, behavior: behavior() });
      setActive(target);
    };

    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          let nearest = 0;
          let best = Infinity;
          slides.forEach((slide, n) => {
            const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
            if (distance < best) {
              best = distance;
              nearest = n;
            }
          });
          if (nearest !== index) setActive(nearest);
        });
      },
      { passive: true }
    );

    thumbs.forEach((thumb, n) => thumb.addEventListener('click', () => goTo(n)));
    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(index - 1);
      }
    });

    // Tap or Enter on an image opens it full screen (native pinch zoom, double-tap for 2x). The dialog helper in
    // assets/pc-audit.js handles Escape, focus containment and returning focus to the image that was opened.
    const zoom = gallery.closest('[data-pc-pdp]') && gallery.closest('[data-pc-pdp]').querySelector('[data-pc-zoom]');
    if (zoom) {
      const zoomImg = zoom.querySelector('[data-pc-zoom-img]');
      const zoomScroll = zoom.querySelector('[data-pc-zoom-scroll]');
      track.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-pc-gal-zoom]');
        if (!opener || !window.PrimeCutDialog) return;
        const img = opener.querySelector('img');
        zoomImg.src = opener.dataset.pcGalZoom;
        zoomImg.alt = img ? img.alt : '';
        zoomScroll.classList.remove('is-zoomed');
        window.PrimeCutDialog.open(zoom, opener);
        zoomScroll.scrollTo(0, 0);
      });
      zoomImg.addEventListener('dblclick', () => zoomScroll.classList.toggle('is-zoomed'));
    }

    gallery.pcGoToMedia = (mediaId) => {
      const n = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (n > -1) goTo(n);
    };
    setActive(0);
  }


  function formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function initNativePicker(root, gallery) {
    const native = root.querySelector('[data-pc-native]');
    const json = root.querySelector('[data-pc-product-json]');
    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    if (!native || !json || !form || native.dataset.pcReady) return;
    native.dataset.pcReady = 'true';
    let variants = [];
    try {
      variants = JSON.parse(json.textContent).variants || [];
    } catch (e) {
      return;
    }
    const idInput = form.querySelector('[name="id"]');
    const button = root.querySelector('[data-pc-atc]');
    const label = button && button.querySelector('.button-text');
    const status = root.querySelector('[data-pc-variant-status]');
    const block = root.querySelector('[data-pc-price-block]');
    const price = (block || native).querySelector('[data-pc-price]');
    const packQty = block ? Number(block.dataset.packQty) || 0 : 0;
    const setText = (selector, text) => {
      const el = block && block.querySelector(selector);
      if (el) el.textContent = text;
    };
    const setAdd = (enabled, text) => {
      idInput.disabled = !enabled;
      if (button) button.disabled = !enabled;
      if (label) label.textContent = text;
    };

    native.addEventListener('change', () => {
      const selected = [...native.querySelectorAll('[data-pc-option]')].map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        const output = fieldset.querySelector('[data-pc-option-value]');
        if (checked && output) output.textContent = checked.value.replace(/\.00$/, '');
        return checked ? checked.value : null;
      });
      const sizeField = native.querySelector('[data-pc-size-option] input:checked');
      root.querySelectorAll('[data-pc-sg-open]').forEach((opener) => {
        opener.dataset.pcSgSize = sizeField ? sizeField.value : '';
      });
      if (selected.includes(null)) {
        setAdd(false, button ? button.dataset.chooseLabel : '');
        return;
      }
      const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));
      if (!variant) {
        setAdd(false, 'Unavailable');
        if (status) status.textContent = 'This combination is unavailable.';
        return;
      }
      idInput.value = variant.id;
      setAdd(variant.available, variant.available ? button.dataset.addLabel : 'Sold out');
      if (button) button.classList.toggle('sold-out', !variant.available);
      if (price) price.textContent = formatMoney(variant.price);
      if (packQty) {
        const compare = variant.compare_at_price || 0;
        const save = compare > variant.price ? compare - variant.price : 0;
        setText('[data-pc-per]', formatMoney(Math.round(variant.price / packQty)));
        setText('[data-pc-save-amount]', formatMoney(save));
        setText('[data-pc-save-pct]', String(save ? Math.round((save * 100) / compare) : 0));
        setText('[data-pc-single]', formatMoney(Math.round(compare / packQty)));
        setText('[data-pc-compare]', formatMoney(compare));
        block.querySelectorAll('[data-pc-save], [data-pc-value]').forEach((el) => {
          el.hidden = !save;
        });
      }
      if (status) status.textContent = `${variant.title} selected, ${formatMoney(variant.price)}${variant.available ? '' : ', sold out'}.`;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      if (variant.featured_media && gallery && gallery.pcGoToMedia) gallery.pcGoToMedia(variant.featured_media.id);
    });

    // The bundle app normally renders its own size/color selection. Only reveal this picker if it never appears.
    const app = root.querySelector('.pc-buy__app');
    if (!native.hidden || !app) return;
    const hasBundle = () => !!app.querySelector('kaching-bundle, [class*="kaching-bundles__"]');
    const observer = new MutationObserver(() => {
      if (hasBundle()) native.hidden = true;
    });
    observer.observe(app, { childList: true, subtree: true });
    window.setTimeout(() => {
      if (!hasBundle()) native.hidden = false;
    }, 5000);
  }

  function initQuantity(root) {
    const wrap = root.querySelector('[data-pc-qty]');
    if (!wrap || wrap.dataset.pcReady) return;
    wrap.dataset.pcReady = 'true';
    const input = wrap.querySelector('input');
    // The quantity the customer asked for. It is tracked apart from the field because the bundle app writes 1 into
    // the field as the form submits (which is why a pack quantity above 1 used to be added as a single pack).
    let wanted = 1;
    const setWanted = (value) => {
      wanted = Math.max(1, Math.round(Number(value)) || 1);
      wrap.dataset.pcWanted = String(wanted);
    };
    setWanted(1);
    const clamp = () => {
      const min = Number(input.min) || 1;
      const max = Number(input.max) || 99;
      const value = Math.round(Number(input.value));
      input.value = String(Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min);
    };
    wrap.querySelectorAll('[data-pc-qty-step]').forEach((btn) =>
      btn.addEventListener('click', () => {
        input.value = String(wanted + Number(btn.dataset.pcQtyStep));
        clamp();
        setWanted(input.value);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
    input.addEventListener('input', (event) => {
      if (event.isTrusted) setWanted(input.value);
    });
    input.addEventListener('blur', clamp);
    // Quantity is reset after a successful add so a second add is not silently multiplied.
    document.addEventListener('cart:updated', () => {
      input.value = '1';
    });

    // After the theme product form has added the pack: if more than one was asked for and the line came back with
    // fewer, set that same line to the chosen quantity. The bundle gifts follow through assets/pc-cart-gift.js
    // (one gift card per pack), exactly as when a quantity is changed in the cart.
    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    // PUB_SUB_EVENTS is a top-level const in constants.js: a global binding, but not a property of window.
    if (!form || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    let pending = 1;
    form.addEventListener(
      'submit',
      () => {
        pending = wanted;
      },
      true
    );
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (!event || event.source !== 'product-form') return;
      const target = pending;
      pending = 1;
      setWanted(1);
      input.value = '1';
      input.dispatchEvent(new Event('change', { bubbles: true }));
      if (target < 2) return;
      try {
        const idInput = form.querySelector('[name="id"]');
        const data = event.cartData || {};
        const lines = Array.isArray(data.items) ? data.items : [data];
        const line = lines.find((item) => item && String(item.variant_id) === String(idInput.value));
        if (!line || !line.key) return;
        const root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((section) => section.id) : [];
        const response = await fetch(`${root_url}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: line.quantity + (target - 1), sections, sections_url: window.location.pathname }),
        });
        const result = await response.json();
        if (response.ok && drawer && typeof drawer.renderContents === 'function' && result.sections) drawer.renderContents(result);
      } catch (e) {
        /* the pack that was added stays in the cart; its quantity can be changed there */
      }
    });
  }

  // One mobile purchase bar per product page. It is shown while the main purchase button is off screen (above or
  // below the viewport) and stays out of the way of the footer, the cart drawer, open dialogs and the cookie banner.
  function initSticky(root) {
    const bar = root.querySelector('[data-pc-sticky]');
    const buy = root.querySelector('.pc-buy');
    if (!bar || !buy || bar.dataset.pcReady || !('IntersectionObserver' in window)) return;
    bar.dataset.pcReady = 'true';
    const action = bar.querySelector('[data-pc-sticky-btn]');
    const main = root.querySelector('[data-bb-atc], [data-pc-atc]');
    let mainVisible = true;
    let footerVisible = false;
    let blocked = false;
    const update = () => {
      const show = !mainVisible && !footerVisible && !blocked;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      document.documentElement.classList.toggle('pc-has-sticky', show);
      if (action) {
        if (show) action.removeAttribute('tabindex');
        else action.setAttribute('tabindex', '-1');
      }
    };
    new IntersectionObserver(([entry]) => {
      mainVisible = entry.isIntersecting;
      update();
    }).observe(main || buy);
    const footer = document.querySelector('.pc-footer, footer');
    if (footer) {
      new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        update();
      }).observe(footer);
    }
    // Overlays: cart drawer / menu drawer (the theme locks the body), any open dialog, Shopify's cookie banner.
    const checkBlocked = () => {
      const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner__dialog');
      const drawer = document.querySelector('cart-drawer.active, cart-drawer.animate, .pc-drawer.is-open');
      blocked =
        document.body.classList.contains('overflow-hidden') ||
        document.documentElement.classList.contains('pc-scroll-lock') ||
        !!document.querySelector('dialog[open]') ||
        !!drawer ||
        !!(banner && banner.offsetParent !== null && getComputedStyle(banner).display !== 'none');
      update();
    };
    if ('MutationObserver' in window) {
      const watch = new MutationObserver(checkBlocked);
      watch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      watch.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      const drawer = document.querySelector('cart-drawer');
      if (drawer) watch.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    document.addEventListener('toggle', checkBlocked, true);
    document.addEventListener('close', checkBlocked, true);
    checkBlocked();

    // Pack and gift-card pages use the theme product form. (The tee's buy box wires this same bar in pc-buybox.js.)
    if (!bar.hasAttribute('data-pc-sticky-native') || !action) return;
    const native = root.querySelector('[data-pc-native]');
    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    const json = root.querySelector('[data-pc-product-json]');
    const qty = root.querySelector('[data-pc-qty] input');
    const priceEl = bar.querySelector('[data-pc-sticky-price]');
    const sumEl = bar.querySelector('[data-pc-sticky-sum]');
    const baseSum = sumEl ? sumEl.textContent.trim() : '';
    let variants = [];
    try {
      variants = JSON.parse(json.textContent).variants || [];
    } catch (e) {
      variants = [];
    }
    const missingOption = () => (native ? [...native.querySelectorAll('[data-pc-option]')].find((fieldset) => !fieldset.querySelector('input:checked')) : null);
    const currentVariant = () => {
      const idInput = form && form.querySelector('[name="id"]');
      return idInput ? variants.find((v) => String(v.id) === String(idInput.value)) : null;
    };
    const sync = () => {
      const missing = missingOption();
      const variant = currentVariant();
      const qtyWrap = root.querySelector('[data-pc-qty]');
      const count = Math.max(1, Number(qtyWrap && qtyWrap.dataset.pcWanted) || Number(qty && qty.value) || 1);
      const busy = !!main && (main.classList.contains('loading') || main.getAttribute('aria-disabled') === 'true');
      if (priceEl && variant) priceEl.textContent = formatMoney(variant.price * count);
      if (sumEl) {
        const size = native && native.querySelector('[data-pc-size-option] input:checked');
        sumEl.textContent = [baseSum, size ? `Size ${size.value}` : null, count > 1 ? `Qty ${count}` : null].filter(Boolean).join(' · ');
      }
      let label = action.dataset.addLabel;
      if (busy) label = 'Adding…';
      else if (missing) label = action.dataset.chooseLabel;
      else if (variant && !variant.available) label = 'Sold out';
      action.textContent = label;
      action.setAttribute('aria-disabled', String(busy || (!missing && !!variant && !variant.available)));
    };
    root.addEventListener('change', sync);
    if (qty) qty.addEventListener('input', sync);
    if (main && 'MutationObserver' in window) {
      new MutationObserver(sync).observe(main, { attributes: true, attributeFilter: ['class', 'aria-disabled', 'disabled'] });
    }
    // A cart error is written next to the main button; bring it into view when the add came from the bar.
    const error = root.querySelector('.product-form__error-message-wrapper');
    if (error && 'MutationObserver' in window) {
      new MutationObserver(() => {
        if (!error.hidden && !mainVisible) error.scrollIntoView({ block: 'center', behavior: behavior() });
      }).observe(error, { attributes: true, attributeFilter: ['hidden'] });
    }
    action.addEventListener('click', () => {
      if (action.getAttribute('aria-disabled') === 'true') return;
      const missing = missingOption();
      if (missing) {
        // Next required choice: bring it into view and focus its first available option.
        missing.scrollIntoView({ block: 'center', behavior: behavior() });
        const first = missing.querySelector('input:not([disabled])');
        if (first) first.focus({ preventScroll: true });
        missing.classList.add('is-prompted');
        window.setTimeout(() => missing.classList.remove('is-prompted'), 1600);
        return;
      }
      if (main && !main.disabled) main.click();
    });
    sync();
  }

  function initBuyLinks(scope) {
    scope.querySelectorAll('[data-pc-buy-link]').forEach((link) => {
      if (link.dataset.pcReady) return;
      link.dataset.pcReady = 'true';
      link.addEventListener('click', (event) => {
        const id = decodeURIComponent((link.getAttribute('href') || '').replace(/^#/, ''));
        const target = id && document.getElementById(id);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: behavior(), block: 'start' });
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  function initVideos(scope) {
    scope.querySelectorAll('[data-pc-video]').forEach((card) => {
      if (card.dataset.pcReady) return;
      card.dataset.pcReady = 'true';
      const button = card.querySelector('[data-pc-video-play]');
      const template = card.querySelector('template');
      if (!button || !template) return;
      button.addEventListener('click', () => {
        let video = card.querySelector('video');
        if (!video) {
          card.appendChild(template.content.cloneNode(true));
          video = card.querySelector('video');
          video.addEventListener('play', () => {
            document.querySelectorAll('.pc-vcard video').forEach((other) => {
              if (other !== video) other.pause();
            });
            card.classList.add('is-playing');
          });
          if ('IntersectionObserver' in window) {
            new IntersectionObserver(([entry]) => {
              if (!entry.isIntersecting) video.pause();
            }).observe(card);
          }
        }
        video.muted = true;
        const attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(() => {});
        video.focus();
      });
    });
  }

  function initReviews(scope) {
    scope.querySelectorAll('[data-pc-reviews]').forEach((section) => {
      if (section.dataset.pcReady) return;
      const list = section.querySelector('[data-pc-review-list]');
      if (!list) return;
      section.dataset.pcReady = 'true';
      const items = [...list.children];
      const perPage = Number(section.dataset.perPage) || 5;
      const more = section.querySelector('[data-pc-review-more]');
      const empty = section.querySelector('[data-pc-review-empty]');
      const status = section.querySelector('[data-pc-review-status]');
      const filters = [...section.querySelectorAll('[data-pc-filter]')];
      const sortSelect = section.querySelector('[data-pc-sort]');
      let filter = 'all';
      let sort = 'recent';
      let shown = perPage;

      const render = (announce) => {
        const matches = items.filter((item) => {
          if (filter === 'all') return true;
          if (filter === 'photos') return Number(item.dataset.photos) > 0;
          return item.dataset.rating === filter;
        });
        const byDate = (a, b) => Number(b.dataset.date) - Number(a.dataset.date);
        matches.sort((a, b) => {
          if (sort === 'high') return Number(b.dataset.rating) - Number(a.dataset.rating) || byDate(a, b);
          if (sort === 'low') return Number(a.dataset.rating) - Number(b.dataset.rating) || byDate(a, b);
          return byDate(a, b);
        });
        items.forEach((item) => {
          item.hidden = true;
        });
        matches.forEach((item, n) => {
          list.appendChild(item);
          item.hidden = n >= shown;
        });
        if (more) more.hidden = matches.length <= shown;
        if (empty) empty.hidden = matches.length > 0;
        if (status && announce) status.textContent = `Showing ${Math.min(shown, matches.length)} of ${matches.length} reviews`;
        return matches;
      };

      filters.forEach((btn) =>
        btn.addEventListener('click', () => {
          const value = btn.dataset.pcFilter;
          filter = filter === value && value !== 'all' ? 'all' : value;
          shown = perPage;
          filters.forEach((other) => other.setAttribute('aria-pressed', String(other.dataset.pcFilter === filter)));
          render(true);
        })
      );
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          sort = sortSelect.value;
          shown = perPage;
          render(true);
        });
      }
      if (more) {
        more.addEventListener('click', () => {
          const firstNew = shown;
          shown += perPage;
          const matches = render(true);
          if (matches[firstNew]) {
            matches[firstNew].setAttribute('tabindex', '-1');
            matches[firstNew].focus();
          }
        });
      }
      render(false);
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-pc-pdp]').forEach((root) => {
      const gallery = root.querySelector('[data-pc-gallery]');
      if (gallery) initGallery(gallery);
      initQuantity(root);
      initNativePicker(root, gallery);
      initSticky(root);
    });
    initBuyLinks(scope);
    initVideos(scope);
    initReviews(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
